import React from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
//
import global from 'common'
import { Formik } from 'formik'
import * as Yup from 'yup'

import Error from 'common/components/dataEntry/formik/FormItemError'
import DefaultInput from 'common/components/dataEntry/formik/FormikDefaultInputField'
import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Button from 'common/components/general/Button'
import { numberOnlyRegx } from 'common/components/utils/Regx'

export const achFormValidator = (translate: any) => {
  return Yup.object({
    bankAccountType: Yup.string().required(translate('general.required')).typeError(translate('general.required')),
    agreeTerms: Yup.bool().oneOf([true], translate('general.required')),
    bankRoutingNumber: Yup.string().trim().required(translate('general.required')),
    accountNumber: Yup.string()
      .required(translate('general.required'))
      .matches(numberOnlyRegx, translate('admin-signup.subscription.errors.account-number-regex')),
    accountNumberConfirmation: Yup.string()
      .oneOf([Yup.ref('accountNumber')], translate('admin-signup.subscription.errors.account-number-confirmation'))
      .required(translate('general.required'))
      .typeError(translate('general.required'))
      .matches(numberOnlyRegx, translate('admin-signup.subscription.errors.account-number-regex')),
    nameOnAccount: Yup.string().trim().required(translate('general.required')),
  })
}

interface ACHPaymentProps {
  onSave: (values: ACHFormValues) => void
  onCancel: () => void
  recurlyErrors: Map<string, string>
}

export interface ACHFormValues {
  bankAccountType: string
  bankRoutingNumber: string
  accountNumber: string
  accountNumberConfirmation: string
  nameOnAccount: string
  billingEmail: string
  agreeTerms: boolean
}

const AchForm: React.FC<ACHPaymentProps> = ({ onSave, onCancel, recurlyErrors }) => {
  const { t: translate } = useTranslation()
  const { isXs } = global.hooks.useBreakPoints()

  return (
    <Formik validationSchema={achFormValidator(translate)} onSubmit={() => {}} initialValues={{}}>
      {(formik) => (
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <Row>
            <Col xs={24} sm={18}>
              <Row className="mb-24">
                <Col xs={24} sm={16} lg={18} xl={16}>
                  <Radio.Group className="radio-group-height-40" name="bankAccountType">
                    <Row gutter={8}>
                      <Col xs={12}>
                        <Radio.Button size="small" className="radio-with-checkmark" value="checking">
                          {translate('admin-signup.subscription.payment.checking-label')}
                        </Radio.Button>
                      </Col>
                      <Col xs={12}>
                        <Radio.Button size="small" className="radio-with-checkmark" value="savings">
                          {translate('admin-signup.subscription.payment.saving-label')}
                        </Radio.Button>
                      </Col>
                    </Row>
                    {recurlyErrors.get('account_type') ? <Error>{recurlyErrors.get('account_type')}</Error> : null}
                  </Radio.Group>
                </Col>
              </Row>

              <Col xs={24} sm={20} className="mb-4">
                <Row gutter={24}>
                  <Col span={24} className="mb-24">
                    <DefaultInput
                      label={translate('admin-signup.subscription.payment.bank-routing-number-label')}
                      name="bankRoutingNumber"
                    />
                    {recurlyErrors.get('routing_number') ? <Error>{recurlyErrors.get('routing_number')}</Error> : null}
                  </Col>

                  <Col xs={24} md={12} xl={24} xxl={12} className="mb-24">
                    <DefaultInput
                      label={translate('admin-signup.subscription.payment.account-number-label')}
                      name="accountNumber"
                    />
                    {recurlyErrors.get('account_number') ? <Error>{recurlyErrors.get('account_number')}</Error> : null}
                  </Col>

                  <Col xs={24} md={12} xl={24} xxl={12} className="mb-24">
                    <DefaultInput
                      label={translate('admin-signup.subscription.payment.confirm-account-number-label')}
                      name="accountNumberConfirmation"
                    />
                    {recurlyErrors.get('account_number_confirmation') ? (
                      <Error>{recurlyErrors.get('account_number_confirmation')}</Error>
                    ) : null}
                  </Col>

                  <Col span={24}>
                    <DefaultInput
                      label={translate('admin-signup.subscription.payment.account-name-label')}
                      name="nameOnAccount"
                    />
                    {recurlyErrors.get('name_on_account') ? (
                      <Error>{recurlyErrors.get('name_on_account')}</Error>
                    ) : null}
                  </Col>
                </Row>
              </Col>
            </Col>

            <Col xs={24} sm={6} className={`flex ${isXs ? 'mb-8' : 'justify-end align-end mb-8'}`}>
              <Button type="link" onClick={() => onCancel()} className="color-gray-8 mr-8">
                {translate('general.cancel')}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  onSave(formik.values as ACHFormValues)
                }}
              >
                {translate('general.save')}
              </Button>
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  )
}

export default AchForm
